const MONTHS = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];

/**
 * Resolve any date-like value into a JS Date in UTC.
 * Handles: ISO strings, Unix timestamps (seconds), Date objects, null/undefined.
 */
const toDate = (value) => {
	if (!value) return null;
	if (value instanceof Date) return value;
	// Unix timestamp in seconds (Stripe-style)
	if (typeof value === "number") return new Date(value * 1000);
	// ISO string or other parseable string
	const d = new Date(value);
	return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Pad a number to 2 digits.
 */
const pad = (n) => String(n).padStart(2, "0");

const formatDateKey = (year, month, day) => `${year}-${pad(month)}-${pad(day)}`;

const parseDateKey = (value) => {
	if (!value || typeof value !== "string") return null;
	const [year, month, day] = value.split("-").map(Number);
	if (!year || !month || !day) return null;
	return { year, month, day };
};

const parseTimeKey = (value) => {
	if (!value || typeof value !== "string") return null;
	const [hours, minutes] = value.split(":").map(Number);
	if (
		Number.isNaN(hours) ||
		Number.isNaN(minutes) ||
		hours < 0 ||
		hours > 23 ||
		minutes < 0 ||
		minutes > 59
	) {
		return null;
	}
	return { hours, minutes };
};

const buildLocalDateTime = (dateKey, timeKey) => {
	const dateParts = parseDateKey(dateKey);
	const timeParts = parseTimeKey(timeKey);
	if (!dateParts || !timeParts) return null;

	return new Date(
		dateParts.year,
		dateParts.month - 1,
		dateParts.day,
		timeParts.hours,
		timeParts.minutes,
		0,
		0,
	);
};

const buildUtcDateTime = (dateKey, timeKey) => {
	const dateParts = parseDateKey(dateKey);
	const timeParts = parseTimeKey(timeKey);
	if (!dateParts || !timeParts) return null;

	return new Date(
		Date.UTC(
			dateParts.year,
			dateParts.month - 1,
			dateParts.day,
			timeParts.hours,
			timeParts.minutes,
			0,
			0,
		),
	);
};

const toLocalScheduleParts = (value) => ({
	date: formatDateKey(
		value.getFullYear(),
		value.getMonth() + 1,
		value.getDate(),
	),
	time: `${pad(value.getHours())}:${pad(value.getMinutes())}`,
	weekday: value.getDay(),
});

const toUtcScheduleParts = (value) => ({
	date: formatDateKey(
		value.getUTCFullYear(),
		value.getUTCMonth() + 1,
		value.getUTCDate(),
	),
	time: `${pad(value.getUTCHours())}:${pad(value.getUTCMinutes())}`,
	weekday: value.getUTCDay(),
});

const normalizeWeekdays = (weekdays = []) =>
	[...new Set(weekdays)]
		.filter((day) => Number.isInteger(day) && day >= 0 && day <= 6)
		.sort((left, right) => left - right);

const getLocalWeekStart = () => {
	const now = new Date();
	const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	start.setHours(0, 0, 0, 0);
	start.setDate(start.getDate() - start.getDay());
	return start;
};

const getUtcWeekStart = () => {
	const now = new Date();
	const start = new Date(
		Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
	);
	start.setUTCHours(0, 0, 0, 0);
	start.setUTCDate(start.getUTCDate() - start.getUTCDay());
	return start;
};

const recurringUtcBoundaryError =
	"This recurring planner window crosses a UTC day boundary. Split it into separate plans.";

const oneShotUtcBoundaryError =
	"This one-time planner window crosses a UTC day boundary. Split it into separate plans.";

const buildRecurringLocalDateKey = (weekday) => {
	const weekStart = getLocalWeekStart();
	const day = new Date(weekStart);
	day.setDate(weekStart.getDate() + weekday);
	return formatDateKey(day.getFullYear(), day.getMonth() + 1, day.getDate());
};

const buildRecurringUtcDateKey = (weekday) => {
	const weekStart = getUtcWeekStart();
	const day = new Date(weekStart);
	day.setUTCDate(weekStart.getUTCDate() + weekday);
	return formatDateKey(
		day.getUTCFullYear(),
		day.getUTCMonth() + 1,
		day.getUTCDate(),
	);
};

export const convertPlannerScheduleToUtc = ({
	scheduleType,
	weekdays,
	scheduledDate,
	startTime,
	endTime,
}) => {
	if (scheduleType === "recurring") {
		const normalizedWeekdays = normalizeWeekdays(weekdays);
		const convertedRanges = normalizedWeekdays.map((weekday) => {
			const dateKey = buildRecurringLocalDateKey(weekday);
			const startLocal = buildLocalDateTime(dateKey, startTime);
			const endLocal = buildLocalDateTime(dateKey, endTime);

			if (!startLocal || !endLocal) {
				throw new Error("Recurring planner time is invalid.");
			}

			const startUtc = toUtcScheduleParts(startLocal);
			const endUtc = toUtcScheduleParts(endLocal);

			if (startUtc.date !== endUtc.date || endUtc.time <= startUtc.time) {
				throw new Error(recurringUtcBoundaryError);
			}

			return {
				weekday: startUtc.weekday,
				startTime: startUtc.time,
				endTime: endUtc.time,
			};
		});

		const referenceRange = convertedRanges[0] || null;
		if (
			referenceRange &&
			convertedRanges.some(
				(range) =>
					range.startTime !== referenceRange.startTime ||
					range.endTime !== referenceRange.endTime,
			)
		) {
			throw new Error(recurringUtcBoundaryError);
		}

		return {
			weekdays: normalizeWeekdays(
				convertedRanges.map((range) => range.weekday),
			),
			scheduledDate: null,
			startTime: referenceRange?.startTime || startTime,
			endTime: referenceRange?.endTime || endTime,
		};
	}

	const startLocal = buildLocalDateTime(scheduledDate, startTime);
	const endLocal = buildLocalDateTime(scheduledDate, endTime);

	if (!startLocal || !endLocal) {
		throw new Error("One-time planner date or time is invalid.");
	}

	const startUtc = toUtcScheduleParts(startLocal);
	const endUtc = toUtcScheduleParts(endLocal);

	if (startUtc.date !== endUtc.date || endUtc.time <= startUtc.time) {
		throw new Error(oneShotUtcBoundaryError);
	}

	return {
		weekdays: null,
		scheduledDate: startUtc.date,
		startTime: startUtc.time,
		endTime: endUtc.time,
	};
};

export const convertPlannerScheduleFromUtc = ({
	scheduleType,
	weekdays,
	scheduledDate,
	startTime,
	endTime,
}) => {
	if (scheduleType === "recurring") {
		const normalizedWeekdays = normalizeWeekdays(weekdays);
		const convertedRanges = normalizedWeekdays
			.map((weekday) => {
				const dateKey = buildRecurringUtcDateKey(weekday);
				const startUtc = buildUtcDateTime(dateKey, startTime);
				const endUtc = buildUtcDateTime(dateKey, endTime);

				if (!startUtc || !endUtc) {
					return null;
				}

				const startLocal = toLocalScheduleParts(startUtc);
				const endLocal = toLocalScheduleParts(endUtc);

				if (
					startLocal.date !== endLocal.date ||
					endLocal.time <= startLocal.time
				) {
					return null;
				}

				return {
					weekday: startLocal.weekday,
					startTime: startLocal.time,
					endTime: endLocal.time,
				};
			})
			.filter(Boolean);

		const referenceRange = convertedRanges[0] || null;
		if (
			!referenceRange ||
			convertedRanges.some(
				(range) =>
					range.startTime !== referenceRange.startTime ||
					range.endTime !== referenceRange.endTime,
			)
		) {
			return {
				weekdays: normalizeWeekdays(weekdays),
				scheduledDate,
				startTime,
				endTime,
			};
		}

		return {
			weekdays: normalizeWeekdays(
				convertedRanges.map((range) => range.weekday),
			),
			scheduledDate: null,
			startTime: referenceRange.startTime,
			endTime: referenceRange.endTime,
		};
	}

	const startUtc = buildUtcDateTime(scheduledDate, startTime);
	const endUtc = buildUtcDateTime(scheduledDate, endTime);

	if (!startUtc || !endUtc) {
		return {
			weekdays: null,
			scheduledDate,
			startTime,
			endTime,
		};
	}

	const startLocal = toLocalScheduleParts(startUtc);
	const endLocal = toLocalScheduleParts(endUtc);

	if (startLocal.date !== endLocal.date || endLocal.time <= startLocal.time) {
		return {
			weekdays: null,
			scheduledDate,
			startTime,
			endTime,
		};
	}

	return {
		weekdays: null,
		scheduledDate: startLocal.date,
		startTime: startLocal.time,
		endTime: endLocal.time,
	};
};

/**
 * Format a UTC date value into the user's local timezone.
 *
 * formatDate("2026-04-02T10:30:00Z")  → "02 April 2026"
 * formatDate(1743580200)              → "02 April 2026"   (Unix timestamp)
 */
export const formatDate = (value) => {
	const d = toDate(value);
	if (!d) return "";
	const day = pad(d.getDate());
	const month = MONTHS[d.getMonth()];
	const year = d.getFullYear();
	return `${day} ${month} ${year}`;
};

/**
 * Format a UTC date value's time portion in 24-hour format in the user's local timezone.
 *
 * formatTime("2026-04-02T14:30:00Z")  → "14:30"
 */
export const formatTime = (value) => {
	const d = toDate(value);
	if (!d) return "";
	return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Format both date and time.
 *
 * formatDateTime("2026-04-02T14:30:00Z")  → "02 April 2026 14:30"
 */
export const formatDateTime = (value) => {
	const d = toDate(value);
	if (!d) return "";
	return `${formatDate(d)} ${formatTime(d)}`;
};

/**
 * Extract just the year from a date value (local timezone).
 *
 * formatYear("2026-04-02")  → 2026
 */
export const formatYear = (value) => {
	const d = toDate(value);
	if (!d) return "";
	return d.getFullYear();
};

/**
 * Relative time label for "recently played" style displays.
 * Falls back to the standard date format for older entries.
 *
 * formatRelativeTime("2026-04-02T14:25:00Z")  → "5m ago"
 */
export const formatRelativeTime = (value) => {
	const d = toDate(value);
	if (!d) return "";
	const now = new Date();
	const diffMs = now - d;
	const diffMinutes = Math.floor(diffMs / (1000 * 60));
	const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

	if (diffMinutes < 1) return "Just now";
	if (diffMinutes < 60) return `${diffMinutes}m ago`;
	if (diffHours < 24) return `${diffHours}h ago`;
	if (diffDays < 7) return `${diffDays}d ago`;

	return formatDate(d);
};

// ---------------------------------------------------------------------------
// Planner schedules
//
// Plans are stored in UTC and always read back through the converters above,
// so every helper here works on the viewer's local wall clock. This file is
// the only place in the web frontend that reasons about dates and times.
// ---------------------------------------------------------------------------

// Display order is Monday-first; `value` keeps the backend's 0=Sunday codes.
export const WEEKDAYS = [
	{ label: "Mon", value: 1, dot: "M" },
	{ label: "Tue", value: 2, dot: "T" },
	{ label: "Wed", value: 3, dot: "W" },
	{ label: "Thu", value: 4, dot: "T" },
	{ label: "Fri", value: 5, dot: "F" },
	{ label: "Sat", value: 6, dot: "S" },
	{ label: "Sun", value: 0, dot: "S" },
];

/** Sort key that puts Sunday (0) at the end of the week. */
export const weekOrder = (day) => (day === 0 ? 7 : day);

export const dayLabel = (value) =>
	WEEKDAYS.find((day) => day.value === value)?.label;

/** Minutes since midnight for an "HH:MM" string; 0 when unparseable. */
export const timeToMinutes = (value) => {
	const parts = parseTimeKey(value);
	return parts ? parts.hours * 60 + parts.minutes : 0;
};

/** "Mon, Tue, Wed" / "Every day" / "2026-09-03" for a list of local weekdays. */
export const formatDays = (scheduleType, weekdays, scheduledDate) => {
	if (scheduleType !== "recurring") {
		return scheduledDate || "Pick a date";
	}
	if (!weekdays || weekdays.length === 0) return "Pick days";
	if (weekdays.length === 7) return "Every day";
	return [...weekdays]
		.sort((a, b) => weekOrder(a) - weekOrder(b))
		.map(dayLabel)
		.filter(Boolean)
		.join(", ");
};

/** A stored plan's days and times in the viewer's own timezone. */
export const localizePlan = (plan) =>
	convertPlannerScheduleFromUtc({
		scheduleType: plan.schedule_type,
		weekdays: plan.weekdays,
		scheduledDate: plan.scheduled_date,
		startTime: plan.start_time,
		endTime: plan.end_time,
	});

/** Local-time schedule line for a stored plan, e.g. "Mon, Tue 09:00-17:00". */
export const formatSchedule = (plan) => {
	const localized = localizePlan(plan);
	const days = formatDays(
		plan.schedule_type,
		localized.weekdays,
		localized.scheduledDate,
	);
	return `${days} ${localized.startTime}-${localized.endTime}`;
};

/** Is this plan inside its window right now, in the viewer's timezone? */
export const isPlanActiveAt = (plan, now = new Date()) => {
	if (!plan) {
		return false;
	}

	if (plan.schedule_type === "recurring" && !plan.is_active) {
		return false;
	}

	const localized = localizePlan(plan);
	const startParts = parseTimeKey(localized.startTime);
	const endParts = parseTimeKey(localized.endTime);
	if (!startParts || !endParts) {
		return false;
	}

	const currentMinutes = now.getHours() * 60 + now.getMinutes();
	const startMinutes = startParts.hours * 60 + startParts.minutes;
	const endMinutes = endParts.hours * 60 + endParts.minutes;
	if (currentMinutes < startMinutes || currentMinutes >= endMinutes) {
		return false;
	}

	if (plan.schedule_type === "recurring") {
		return (localized.weekdays || []).includes(now.getDay());
	}

	return (
		localized.scheduledDate ===
		formatDateKey(now.getFullYear(), now.getMonth() + 1, now.getDate())
	);
};

/** Oldest plan whose window covers `now`, or null. */
export const findActivePlan = (plans = [], now = new Date()) =>
	[...plans]
		.sort((left, right) =>
			String(left.created_at || "").localeCompare(
				String(right.created_at || ""),
			),
		)
		.find((plan) => isPlanActiveAt(plan, now)) || null;

/**
 * The viewer's current UTC offset, shown next to a schedule so nobody reads a
 * plan's times in the wrong timezone. "UTC+2", "UTC-3:30", "UTC" at zero.
 */
export const formatUtcOffset = (now = new Date()) => {
	const totalMinutes = -now.getTimezoneOffset();
	if (totalMinutes === 0) return "UTC";
	const hours = Math.floor(Math.abs(totalMinutes) / 60);
	const minutes = Math.abs(totalMinutes) % 60;
	const sign = totalMinutes < 0 ? "-" : "+";
	return `UTC${sign}${hours}${minutes ? `:${pad(minutes)}` : ""}`;
};
